import { FilterField, filterControlClass } from '@/components/admin/versioned/filter-field';
import { EXAMPLES_FILTER_LABEL, SCORING_FILTER_LABEL } from '@/lib/criterion-labels';
import { CRITERION_WORDING } from '@/lib/criterion-wording';
import type {
  CriterionExamplesFilter,
  CriterionFilters,
  CriterionScoringFilter,
} from '@matter_extractor/shared';
import {
  criterionExamplesFilterSchema,
  criterionScoringFilterSchema,
} from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * The criterion's OWN filters, at the head of « Plus de filtres » (refonte
 * modulaire §6).
 *
 * TWO controls, and they are the only ones of the whole panel that are not
 * shared by the four versioned objects: how many positive examples feed the
 * scribe, and whether the criterion is scored. §4 gives the rest - the two
 * periods and the four boxes - to every library, so they live in
 * `versioned/filter-panel.tsx` and this file fills its slot.
 *
 * They sit FIRST in the row because they are what somebody opened the panel
 * for; a persona puts its « présentation » and its « cran » in the same place.
 *
 * « Notation » is a filter and not a sort (§6), which is why nothing about it
 * reaches `SORT_LABEL`: « toutes, notés, non notés » answers a question about
 * ONE criterion, where a sort would have to rank two of them by a scale whose
 * band count means nothing next to another's.
 */

export interface CriterionFilterFieldsProps {
  filters: CriterionFilters;
  onChange: (patch: Partial<CriterionFilters>) => void;
}

export function CriterionFilterFields({ filters, onChange }: CriterionFilterFieldsProps) {
  const examplesId = useId();
  const scoringId = useId();
  return (
    <>
      <FilterField label="Exemples" htmlFor={examplesId} className="w-40">
        <select
          id={examplesId}
          data-testid={`${CRITERION_WORDING.entity}-filtre-exemples`}
          value={filters.examples}
          onChange={(event) =>
            onChange({ examples: event.currentTarget.value as CriterionExamplesFilter })
          }
          className={filterControlClass(filters.examples !== 'all', 'w-full')}
        >
          {criterionExamplesFilterSchema.options.map((option) => (
            <option key={option} value={option}>
              {EXAMPLES_FILTER_LABEL[option]}
            </option>
          ))}
        </select>
      </FilterField>

      <FilterField label="Notation" htmlFor={scoringId} className="w-40">
        <select
          id={scoringId}
          data-testid={`${CRITERION_WORDING.entity}-filtre-notation`}
          value={filters.scoring}
          onChange={(event) =>
            onChange({ scoring: event.currentTarget.value as CriterionScoringFilter })
          }
          className={filterControlClass(filters.scoring !== 'all', 'w-full')}
        >
          {criterionScoringFilterSchema.options.map((option) => (
            <option key={option} value={option}>
              {SCORING_FILTER_LABEL[option]}
            </option>
          ))}
        </select>
      </FilterField>
    </>
  );
}
